import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "./PasswordInput";
import { FormError } from "./FormError";
import { toast } from "sonner";

/**
 * Reset Password Form Component
 *
 * Form to set a new password after clicking reset link.
 */
export function ResetPasswordForm() {
  const { updatePassword, loading, session } = useAuth();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    password?: string;
    confirmPassword?: string;
  }>({});

  // Check if user has a valid recovery session
  useEffect(() => {
    // Supabase automatically handles the recovery token from URL
    // and creates a session. If no session exists after a moment,
    // the link might be expired or invalid.
    const checkSession = setTimeout(() => {
      if (!session) {
        setError(
          "This password reset link is invalid or has expired. Please request a new one."
        );
      }
    }, 2000);

    return () => clearTimeout(checkSession);
  }, [session]);

  const validateForm = (): boolean => {
    const newErrors: {
      password?: string;
      confirmPassword?: string;
    } = {};

    if (!password) {
      newErrors.password = "Password is required";
    } else if (password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords don't match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) return;

    try {
      await updatePassword(password);
      toast.success("Password updated successfully!");
      navigate({ to: "/login" });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to update password. Please try again.";
      setError(message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormError message={error} />

      <p className="text-sm text-muted-foreground">
        Enter your new password below.
      </p>

      <PasswordInput
        label="New password"
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setErrors((prev) => ({ ...prev, password: undefined }));
        }}
        error={errors.password}
        disabled={loading}
        showStrengthIndicator
      />

      <PasswordInput
        id="confirm-new-password"
        label="Confirm new password"
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }}
        error={errors.confirmPassword}
        disabled={loading}
      />

      <Button type="submit" className="w-full h-11" disabled={loading}>
        {loading ? (
          <>
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
            Updating...
          </>
        ) : (
          "Update password"
        )}
      </Button>
    </form>
  );
}

export default ResetPasswordForm;
